import { useEffect, useMemo, useState } from "react";
import type { AccessLevel, ForteXApi, MapNodeKindName, MapRootName, MasterDataModel, PublicMapDetail, PublicMapUpdate, RoleFeatureGrant } from "@fortex/api";
import { Button, Field, Input, Skeleton, Switch, TextArea, nameOf, resolveKind } from "@fortex/map";
import { useApp } from "../../app/context";
import { useLoad } from "../../app/useLoad";
import { uiFeatures } from "../../auth/store";
import type { AppConfig } from "../../config";
import type { AppLabels } from "../../i18n";
import type { FieldErrors } from "../shared/CrudPage";

export type PublicMapLayerDraft = { idName: string; defaultOn: boolean; searchable: boolean };

export type PublicMapDraft = {
  id: string | null;
  name: string;
  description: string;
  searchLabel: string;
  lat: string;
  lon: string;
  zoom: string;
  layers: PublicMapLayerDraft[];
  features: RoleFeatureGrant[] | null;
};

export type TreeLeaf = { idName: string; name: string; kind: MapNodeKindName; root: MapRootName; group: string };

export function emptyPublicMapDraft(): PublicMapDraft {
  return { id: null, name: "", description: "", searchLabel: "", lat: "", lon: "", zoom: "", layers: [], features: null };
}

export function draftOfDetail(detail: PublicMapDetail): PublicMapDraft {
  return {
    id: detail.id,
    name: detail.name,
    description: detail.description ?? "",
    searchLabel: detail.searchLabel ?? "",
    lat: detail.lat === null ? "" : String(detail.lat),
    lon: detail.lon === null ? "" : String(detail.lon),
    zoom: detail.zoom === null ? "" : String(detail.zoom),
    layers: detail.layers.map(a => ({ idName: a.idName, defaultOn: a.defaultOn, searchable: a.searchable })),
    features: detail.features
  };
}

export function grantedUiFeatures(template: RoleFeatureGrant[], level: AccessLevel = "Full"): RoleFeatureGrant[] {
  const ui = new Set<string>(Object.values(uiFeatures));

  return template.map(a => (ui.has(a.featureIdName) ? { ...a, granted: true, accessLevel: level } : { ...a, granted: false, accessLevel: "None" }));
}

function numberOf(value: string): number | null {
  const trimmed = value.trim().replace(",", ".");

  if (trimmed.length === 0) return null;

  const parsed = Number(trimmed);

  return Number.isFinite(parsed) ? parsed : Number.NaN;
}

export function validatePublicMapDraft(draft: PublicMapDraft, labels: AppLabels): FieldErrors {
  const errors: FieldErrors = {};
  const lat = numberOf(draft.lat);
  const lon = numberOf(draft.lon);
  const zoom = numberOf(draft.zoom);

  if (draft.name.trim().length === 0) errors.name = labels.publicMapNameRequired;
  if (draft.layers.length === 0) errors.layers = labels.publicMapNeedsLayer;

  const latBad = lat !== null && (Number.isNaN(lat) || lat < -90 || lat > 90);
  const lonBad = lon !== null && (Number.isNaN(lon) || lon < -180 || lon > 180);
  const zoomBad = zoom !== null && (Number.isNaN(zoom) || zoom < 0 || zoom > 24 || !Number.isInteger(zoom));

  if (latBad || lonBad || zoomBad || (lat === null) !== (lon === null)) errors.view = labels.publicMapViewInvalid;

  return errors;
}

export function updateOfDraft(draft: PublicMapDraft): PublicMapUpdate {
  return {
    name: draft.name.trim(),
    description: draft.description.trim().length === 0 ? null : draft.description.trim(),
    searchLabel: draft.searchLabel.trim().length === 0 ? null : draft.searchLabel.trim(),
    lat: numberOf(draft.lat),
    lon: numberOf(draft.lon),
    zoom: numberOf(draft.zoom),
    layers: draft.layers.map((a, index) => ({ idName: a.idName, defaultOn: a.defaultOn, searchable: a.searchable, order_: index })),
    features: (draft.features ?? []).map(a => ({ featureId: a.featureId, granted: a.granted && a.accessLevel !== "None", accessLevel: a.granted ? a.accessLevel : "None" }))
  };
}

export function publicMapLink(config: Pick<AppConfig, "basePath">, id: string, origin: string = typeof window === "undefined" ? "" : window.location.origin): string {
  const base = config.basePath === "/" ? "" : config.basePath;

  return `${origin}${base}/?publicmapid=${encodeURIComponent(id)}`;
}

export function treeLeaves(rows: MasterDataModel[], root: MapRootName, langId: number): TreeLeaf[] {
  const byId = new Map(rows.map(a => [a.id, a]));
  const parents = new Set(rows.map(a => a.parentMasterData_Id).filter((a): a is number => a !== null));
  const rootRow = rows.find(a => a.idName === root) ?? null;
  const leaves: TreeLeaf[] = [];

  for (const row of rows) {
    if (row === rootRow || parents.has(row.id)) continue;

    const { kind } = resolveKind(row.value, false);

    if (kind === "group") continue;

    const parent = row.parentMasterData_Id === null ? null : byId.get(row.parentMasterData_Id) ?? null;
    const group = parent === null || parent === rootRow ? "" : nameOf(parent, langId);

    leaves.push({ idName: row.idName, name: nameOf(row, langId), kind: kindName(kind), root, group });
  }

  return leaves;
}

function kindName(kind: string): MapNodeKindName {
  switch (kind) {
    case "tile":
      return "Tile";
    case "special":
      return "Special";
    case "folder":
      return "Folder";
    case "group":
      return "Group";
    default:
      return "GeoObject";
  }
}

export type PublicMapFormProps = {
  api: ForteXApi;
  labels: AppLabels;
  draft: PublicMapDraft;
  setDraft: (draft: PublicMapDraft) => void;
  errors: FieldErrors;
  readOnly: boolean;
  link: string | null;
};

const accessLevels: Exclude<AccessLevel, "None">[] = ["ReadOnly", "Full"];

export function PublicMapForm({ api, labels, draft, setDraft, errors, readOnly, link }: PublicMapFormProps) {
  const { toast, lang } = useApp();
  const langId = lang === "en" ? 2 : 1;
  const maps = useLoad(signal => api.tree("MAP", signal), []);
  const layers = useLoad(signal => api.tree("LAYER", signal), []);
  const template = useLoad(signal => (draft.features === null ? api.publicMapFeatures(signal) : Promise.resolve(draft.features)), [draft.id]);
  const [copied, setCopied] = useState(false);
  const leaves = useMemo(() => [...treeLeaves(maps.data ?? [], "MAP", langId), ...treeLeaves(layers.data ?? [], "LAYER", langId)], [maps.data, layers.data, langId]);
  const groups = useMemo(() => {
    const result = new Map<string, TreeLeaf[]>();

    for (const leaf of leaves) {
      const key = `${leaf.root}:${leaf.group}`;

      result.set(key, [...(result.get(key) ?? []), leaf]);
    }

    return [...result.entries()];
  }, [leaves]);
  const features = draft.features ?? template.data ?? null;

  useEffect(() => {
    if (draft.features === null && template.data !== null) setDraft({ ...draft, features: grantedUiFeatures(template.data) });
  }, [draft, setDraft, template.data]);

  const layerOf = (idName: string) => draft.layers.find(a => a.idName === idName) ?? null;

  const toggleLayer = (leaf: TreeLeaf, on: boolean) => {
    const rest = draft.layers.filter(a => a.idName !== leaf.idName);

    setDraft({ ...draft, layers: on ? [...rest, { idName: leaf.idName, defaultOn: true, searchable: false }] : rest });
  };

  const patchLayer = (idName: string, patch: Partial<PublicMapLayerDraft>) => {
    setDraft({ ...draft, layers: draft.layers.map(a => (a.idName === idName ? { ...a, ...patch } : a)) });
  };

  const patchFeature = (featureId: number, patch: Partial<RoleFeatureGrant>) => {
    if (features === null) return;

    setDraft({ ...draft, features: features.map(a => (a.featureId === featureId ? { ...a, ...patch } : a)) });
  };

  const copy = async () => {
    if (link === null) return;

    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast("success", labels.publicMapLinkCopied);
    } catch {
      setCopied(false);
    }
  };

  return (
    <form className="fxapp-form" onSubmit={event => event.preventDefault()} noValidate data-fxmap="public-map-form">
      {link !== null ? (
        <section className="fxapp-form-section">
          <h3 className="fxapp-settings-title">{labels.publicMapLink}</h3>
          <div className="fxapp-link-row">
            <Input readOnly value={link} mono onFocus={event => event.target.select()} data-fxmap="public-map-link" />
            <Button kind="ghost" size="sm" icon={copied ? "check" : "copy"} label={labels.copyLink} onClick={() => void copy()} data-fxmap="public-map-copy" />
            <Button kind="ghost" size="sm" icon="external" label={labels.publicMapOpen} onClick={() => window.open(link, "_blank", "noopener")} />
          </div>
        </section>
      ) : null}
      <section className="fxapp-form-section">
        <h3 className="fxapp-settings-title">{labels.publicMapBasics}</h3>
        <div className="fxapp-form-row">
          <Field label={labels.publicMapName} required error={errors.name ?? null}>
            <Input value={draft.name} readOnly={readOnly} maxLength={200} onChange={event => setDraft({ ...draft, name: event.target.value })} data-fxmap="public-map-name" />
          </Field>
          <Field label={labels.publicMapDescription}>
            <TextArea value={draft.description} readOnly={readOnly} rows={2} onChange={event => setDraft({ ...draft, description: event.target.value })} />
          </Field>
          <Field label={labels.publicMapSearchLabel} hint={labels.publicMapSearchLabelHint}>
            <Input value={draft.searchLabel} readOnly={readOnly} maxLength={500} onChange={event => setDraft({ ...draft, searchLabel: event.target.value })} data-fxmap="public-map-search-label" />
          </Field>
        </div>
      </section>
      <section className="fxapp-form-section">
        <h3 className="fxapp-settings-title">{labels.publicMapView}</h3>
        <div className="fxapp-form-row">
          <Field label={labels.publicMapLat} error={errors.view ?? null}>
            <Input inputMode="decimal" value={draft.lat} readOnly={readOnly} onChange={event => setDraft({ ...draft, lat: event.target.value })} data-fxmap="public-map-lat" />
          </Field>
          <Field label={labels.publicMapLon}>
            <Input inputMode="decimal" value={draft.lon} readOnly={readOnly} onChange={event => setDraft({ ...draft, lon: event.target.value })} data-fxmap="public-map-lon" />
          </Field>
          <Field label={labels.publicMapZoom}>
            <Input inputMode="numeric" value={draft.zoom} readOnly={readOnly} onChange={event => setDraft({ ...draft, zoom: event.target.value })} data-fxmap="public-map-zoom" />
          </Field>
        </div>
      </section>
      <section className="fxapp-form-section">
        <h3 className="fxapp-settings-title">{labels.publicMapLayers}</h3>
        <Field label={labels.publicMapLayersHint} error={errors.layers ?? null}>
          {maps.loading || layers.loading ? (
            <Skeleton lines={4} />
          ) : (
            <ul className="fxapp-checklist" data-fxmap="public-map-layers">
              {groups.map(([key, items]) => (
                <li key={key}>
                  {items[0].group.length > 0 ? <div className="fxapp-checklist-group">{items[0].group}</div> : null}
                  <ul className="fxapp-checklist">
                    {items.map(leaf => {
                      const picked = layerOf(leaf.idName);

                      return (
                        <li key={leaf.idName} className="fxapp-checklist-row" data-fxmap-layer={leaf.idName}>
                          <label className="fxapp-checklist-label">
                            <input type="checkbox" checked={picked !== null} disabled={readOnly} onChange={event => toggleLayer(leaf, event.target.checked)} />
                            <span className="fxapp-checklist-name" title={leaf.idName}>
                              {leaf.name}
                            </span>
                          </label>
                          {picked !== null ? <Switch checked={picked.defaultOn} disabled={readOnly} onChange={defaultOn => patchLayer(leaf.idName, { defaultOn })} label={labels.publicMapDefaultOn} showLabel /> : <span />}
                          {picked !== null && leaf.kind === "Folder" ? <Switch checked={picked.searchable} disabled={readOnly} onChange={searchable => patchLayer(leaf.idName, { searchable })} label={labels.publicMapSearchable} showLabel /> : <span />}
                        </li>
                      );
                    })}
                  </ul>
                </li>
              ))}
            </ul>
          )}
        </Field>
      </section>
      <section className="fxapp-form-section">
        <h3 className="fxapp-settings-title">{labels.publicMapFeatures}</h3>
        {features === null ? (
          <Skeleton lines={4} />
        ) : (
          <div className="fxapp-role-list" data-fxmap="public-map-features">
            {features.map(feature => (
              <div key={feature.featureId} className="fxapp-feature-row" data-fxmap-feature={feature.featureIdName}>
                <Switch checked={feature.granted} disabled={readOnly} onChange={granted => patchFeature(feature.featureId, { granted, accessLevel: granted ? (feature.accessLevel === "None" ? "Full" : feature.accessLevel) : "None" })} label={feature.featureName} showLabel />
                {feature.granted ? (
                  <select className="fxmap-select fxapp-select" value={feature.accessLevel === "None" ? "Full" : feature.accessLevel} disabled={readOnly} aria-label={labels.accessLevel} onChange={event => patchFeature(feature.featureId, { accessLevel: event.target.value as AccessLevel })}>
                    {accessLevels.map(level => (
                      <option key={level} value={level}>
                        {level === "Full" ? labels.full : labels.readOnly}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span />
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </form>
  );
}
